/**
 * @fileoverview Section "Inputs" — composant shadcn `<Input>` GNPC (44 px
 * minimum) + wrapper `<FormField>` qui fournit label + tooltip d'aide +
 * message d'erreur avec icône. Les inputs métier (`PhoneNumberInput`,
 * `OtpInput`, `PlateInput`, `AmountInput`, `LiquidationNumber`) auront
 * leur propre section une fois livrés.
 */

import { useState } from 'react';
import { FormField, Input, PasswordInput, ReasonTextarea, SearchInput } from '@gnpc/ui';
import { Search, Mail, Phone, Lock, KeyRound } from 'lucide-react';
import { DsPreview } from '../components/DsPreview.tsx';

/**
 * Validation de plaque CI — 2 lettres + 3 chiffres + 2 lettres,
 * espaces optionnels à la saisie mais canonicalisés avant test.
 * Ex : `AB 572 GG`, `AB572GG`, `ab572gg`.
 */
const PLATE_PATTERN = /^[A-Za-z]{2}\d{3}[A-Za-z]{2}$/;

/**
 * Petite démo interactive : le champ est en erreur tant que la valeur
 * ne matche pas le format plaque CI. Corriger la valeur → l'erreur
 * disparaît en direct (aria-invalid basculé + message enlevé).
 */
function LivePlateValidation(): JSX.Element {
  const [value, setValue] = useState('AAA-invalide');
  const trimmed = value.replace(/\s+/g, '');
  const isEmpty = trimmed.length === 0;
  const isValid = PLATE_PATTERN.test(trimmed);
  const error = isEmpty
    ? 'La plaque est obligatoire.'
    : isValid
      ? undefined
      : 'Format invalide — attendu : AB 572 GG.';
  return (
    <div className="w-full">
      <FormField
        label="Plaque d'immatriculation"
        required
        tooltip="Format Côte d'Ivoire : 2 lettres, 3 chiffres, 2 lettres. Ex : AB 572 GG."
        hint="Corrigez la valeur — le message disparaît dès que le format devient valide."
        error={error}
      >
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value.toUpperCase())}
          placeholder="AB 572 GG"
        />
      </FormField>
    </div>
  );
}

export function DsSectionInputs(): JSX.Element {
  return (
    <div className="space-y-8">
      {/* ---------------------------------------------------------------- */}
      <div className="space-y-3">
        <h3 className="text-quipux-navy-950 text-sm font-semibold">Tailles</h3>
        <p className="text-muted-foreground text-xs">
          Défaut <code>md</code> = 44 px (cible tactile GNPC). <code>sm</code> réservé aux
          formulaires backoffice denses ; <code>lg</code> aux CTA portail mobile.
        </p>
        <div className="grid gap-4 lg:grid-cols-3">
          <DsPreview label='size="sm" — h-9, dense'>
            <div className="w-full">
              <Input size="sm" placeholder="Recherche rapide" />
            </div>
          </DsPreview>
          <DsPreview label='size="md" — h-11, défaut'>
            <div className="w-full">
              <Input placeholder="Saisir une valeur" />
            </div>
          </DsPreview>
          <DsPreview label='size="lg" — h-12, mobile / hero'>
            <div className="w-full">
              <Input size="lg" placeholder="Téléphone du compte Quipux" />
            </div>
          </DsPreview>
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      <div className="space-y-3">
        <h3 className="text-quipux-navy-950 text-sm font-semibold">Avec icône</h3>
        <div className="grid gap-4 lg:grid-cols-2">
          <DsPreview label="leftIcon — indicateur contextuel">
            <div className="w-full">
              <Input leftIcon={<Search />} placeholder="Rechercher une transaction…" />
            </div>
          </DsPreview>
          <DsPreview label="rightIcon — action ou statut">
            <div className="w-full">
              <Input rightIcon={<Lock />} type="password" placeholder="Mot de passe" />
            </div>
          </DsPreview>
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      <div className="space-y-3">
        <h3 className="text-quipux-navy-950 text-sm font-semibold">Wrappers backoffice</h3>
        <p className="text-muted-foreground text-xs">
          `SearchInput`, `PasswordInput` et `ReasonTextarea` couvrent les usages répétés de la
          console : filtres DataTable, login agent et actions sensibles journalisées.
        </p>
        <div className="grid gap-4 lg:grid-cols-3">
          <DsPreview label="SearchInput — toolbar DataTable">
            <div className="w-full">
              <SearchInput placeholder="Référence, téléphone, agent…" defaultValue="PAY" />
            </div>
          </DsPreview>
          <DsPreview label="PasswordInput — login agent">
            <div className="w-full">
              <PasswordInput minLength={12} autoComplete="current-password" placeholder="Mot de passe" />
            </div>
          </DsPreview>
          <DsPreview label="ReasonTextarea — motif sensible">
            <div className="w-full">
              <ReasonTextarea
                defaultValue="Timeout STI après débit confirmé."
                aria-label="Motif de rejeu STI"
              />
            </div>
          </DsPreview>
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      <div className="space-y-3">
        <h3 className="text-quipux-navy-950 text-sm font-semibold">
          FormField — label + tooltip d'aide + hint / erreur
        </h3>
        <p className="text-muted-foreground text-xs">
          Wrapper à utiliser par défaut dans les pages métier. Gère l'association
          <code> label / input</code> (a11y), le tooltip d'aide, et bascule automatiquement en état
          erreur (icône <code>AlertCircle</code> + message).
        </p>

        <div className="grid gap-6 lg:grid-cols-2">
          <DsPreview label="Nominal — hint permanent + tooltip d'aide">
            <FormField
              label="Email professionnel"
              required
              tooltip="Adresse @quipux.ci uniquement. Utilisée pour l'authentification MFA et le journal d'audit."
              hint="Saisir l'adresse fournie par l'administrateur."
            >
              <Input
                type="email"
                autoComplete="email"
                leftIcon={<Mail />}
                placeholder="agent@quipux.ci"
              />
            </FormField>
          </DsPreview>

          <DsPreview label="Erreur DYNAMIQUE — disparaît quand la valeur devient valide">
            <LivePlateValidation />
          </DsPreview>

          <DsPreview label="Téléphone avec tooltip — compte Quipux">
            <FormField
              label="Téléphone du compte Quipux"
              required
              tooltip="Numéro Côte d'Ivoire associé au compte Quipux. Ce numéro reçoit l'OTP par SMS."
              hint="Format E.164 attendu par l'API."
            >
              <Input
                type="tel"
                inputMode="tel"
                leftIcon={<Phone />}
                placeholder="+225 07 00 00 00 00"
              />
            </FormField>
          </DsPreview>

          <DsPreview label="Mot de passe backoffice — minLength 12">
            <FormField
              label="Mot de passe"
              required
              tooltip="12 caractères minimum. Aucune règle de composition (Argon2id + MFA TOTP suffisent — cf. Guide Go §RBAC)."
              hint="Sera vérifié en Argon2id côté serveur."
            >
              <Input
                type="password"
                minLength={12}
                autoComplete="new-password"
                leftIcon={<KeyRound />}
                placeholder="12 caractères minimum"
              />
            </FormField>
          </DsPreview>
        </div>
      </div>

      {/* ---------------------------------------------------------------- */}
      <div className="space-y-3">
        <h3 className="text-quipux-navy-950 text-sm font-semibold">États</h3>
        <div className="grid gap-4 lg:grid-cols-3">
          <DsPreview label="focus-visible — Tab clavier">
            <div className="w-full">
              <Input placeholder="Cliquer / Tab" autoFocus />
            </div>
          </DsPreview>
          <DsPreview label="disabled">
            <div className="w-full">
              <Input placeholder="Indisponible" disabled />
            </div>
          </DsPreview>
          <DsPreview label="Combinaison — leftIcon + disabled">
            <div className="w-full">
              <Input leftIcon={<Search />} placeholder="Recherche verrouillée" disabled />
            </div>
          </DsPreview>
        </div>
      </div>
    </div>
  );
}
